import {
  useDroppable,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import { BlockPreview } from './BlockPreview'
import { EditorialGridColumns } from './EditorialGridColumns'
import type { CanvasSlot, ColKey, PickerMode } from './canvasTypes'
import type { BlockCanvasState } from './useBlockCanvas'
import type { PageBlockType } from '../../api/types'

// ─── Sortable block row ───────────────────────────────────────────────────────

interface SortableBlockRowProps {
  slot: CanvasSlot
  idx: number
  totalSlots: number
  isSelected: boolean
  isAssigningThisRow: boolean
  pickerMode: PickerMode
  selectedColKey: ColKey | null
  selectedColBlockId: string | null
  onSelect: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
  onColumnClick: (colKey: ColKey, colLabel: string) => void
  onColBlockSelect: (colKey: ColKey, blockId: string, blockType: PageBlockType) => void
  onRemoveColBlock: (colKey: ColKey, blockId: string) => void
  onMoveColBlock: (colKey: ColKey, blockId: string, direction: 'up' | 'down') => void
}

function SortableBlockRow({
  slot,
  idx,
  totalSlots,
  isSelected,
  isAssigningThisRow,
  pickerMode,
  selectedColKey,
  selectedColBlockId,
  onSelect,
  onMoveUp,
  onMoveDown,
  onRemove,
  onColumnClick,
  onColBlockSelect,
  onRemoveColBlock,
  onMoveColBlock,
}: SortableBlockRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slot.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  }

  const def = PAGE_BLOCK_REGISTRY[slot.type]

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={`cursor-pointer overflow-hidden rounded border-2 transition-colors ${
        isSelected ? 'border-line-strong' : 'border-line hover:border-line-strong'
      }${isAssigningThisRow ? ' ring-2 ring-blue-500 ring-offset-1' : ''}`}
    >
      {/* Chrome header — entire bar is the drag handle */}
      <div
        className="flex touch-none cursor-grab items-center gap-2 border-b border-line bg-surface px-2 py-1.5 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        {/* Grip icon — visual indicator only */}
        <span className="shrink-0 text-fg-secondary opacity-50" aria-hidden="true">
          <GripVertical size={14} />
        </span>
        <span className="w-5 text-center text-xs font-bold text-fg-secondary">{idx + 1}</span>
        <div className="flex-1">
          <span className="text-xs font-semibold text-fg">{def.label}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={idx === 0}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onMoveUp() }}
            className="rounded px-1.5 py-0.5 text-xs text-fg-secondary hover:bg-solid hover:text-white disabled:opacity-30"
          >
            ↑
          </button>
          <button
            type="button"
            disabled={idx === totalSlots - 1}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onMoveDown() }}
            className="rounded px-1.5 py-0.5 text-xs text-fg-secondary hover:bg-solid hover:text-white disabled:opacity-30"
          >
            ↓
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onRemove() }}
            className="rounded px-1.5 py-0.5 text-xs text-fg-secondary hover:bg-red-9 hover:text-white"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Block body */}
      {slot.type === 'editorial-grid' ? (
        <EditorialGridColumns
          slot={slot}
          assigningColKey={
            isAssigningThisRow ? (pickerMode as { kind: 'assign'; colKey: ColKey }).colKey : null
          }
          selectedColKey={isSelected ? selectedColKey : null}
          selectedColBlockId={isSelected ? selectedColBlockId : null}
          onColumnClick={onColumnClick}
          onColBlockSelect={onColBlockSelect}
          onRemoveColBlock={onRemoveColBlock}
          onMoveColBlock={onMoveColBlock}
        />
      ) : (
        <div className="pointer-events-none bg-canvas">
          <BlockPreview type={slot.type} config={slot.config} />
        </div>
      )}
    </div>
  )
}

// ─── BlockCanvas ──────────────────────────────────────────────────────────────

interface BlockCanvasProps {
  canvas: BlockCanvasState
  headerLabel?: string
  emptyMessage?: string
}

export function BlockCanvas({
  canvas,
  headerLabel = 'Layout Structure',
  emptyMessage = 'Add rows from the left shelf to build a layout.',
}: BlockCanvasProps) {
  const {
    slots,
    selectedId,
    pickerMode,
    selectedColKey,
    selectedColBlockId,
    setSelectedId,
    setSelectedColumnType,
    setSelectedColKey,
    setSelectedColBlockId,
    setPickerMode,
    moveBlock,
    removeBlock,
    removeColBlock,
    moveColBlock,
  } = canvas

  // Drop target for the empty-state placeholder — shelf drags need somewhere to land.
  const { setNodeRef: setEmptyDropRef, isOver: isOverEmpty } = useDroppable({ id: 'canvas' })

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-fg-secondary">{headerLabel}</h2>
        <span className="text-xs text-fg-secondary">
          {slots.length} block{slots.length !== 1 ? 's' : ''}
        </span>
      </div>

      {slots.length === 0 ? (
        <div
          ref={setEmptyDropRef}
          className={`flex h-48 items-center justify-center rounded border border-dashed text-sm transition-colors ${
            isOverEmpty
              ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
              : 'border-line text-fg-secondary'
          }`}
        >
          {isOverEmpty ? 'Drop to add' : emptyMessage}
        </div>
      ) : (
        <SortableContext items={slots.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-6">
            {slots.map((slot, idx) => {
              const isSelected = slot.id === selectedId
              const isAssigningThisRow = pickerMode.kind === 'assign' && pickerMode.slotId === slot.id
              return (
                <SortableBlockRow
                  key={slot.id}
                  slot={slot}
                  idx={idx}
                  totalSlots={slots.length}
                  isSelected={isSelected}
                  isAssigningThisRow={isAssigningThisRow}
                  pickerMode={pickerMode}
                  selectedColKey={selectedColKey}
                  selectedColBlockId={selectedColBlockId}
                  onSelect={() => {
                    setSelectedId(slot.id)
                    setSelectedColumnType(null)
                    setSelectedColKey(null)
                    if (pickerMode.kind === 'assign' && pickerMode.slotId !== slot.id) {
                      setPickerMode({ kind: 'add' })
                    }
                  }}
                  onMoveUp={() => moveBlock(slot.id, 'up')}
                  onMoveDown={() => moveBlock(slot.id, 'down')}
                  onRemove={() => removeBlock(slot.id)}
                  onColumnClick={(colKey, colLabel) =>
                    setPickerMode({ kind: 'assign', slotId: slot.id, colKey, colLabel, target: 'layout' })
                  }
                  onColBlockSelect={(colKey, blockId, blockType) => {
                    setSelectedId(slot.id)
                    setSelectedColKey(colKey)
                    setSelectedColBlockId(blockId)
                    setSelectedColumnType(blockType)
                  }}
                  onRemoveColBlock={(colKey, blockId) => {
                    removeColBlock(slot.id, colKey, blockId)
                    if (selectedColKey === colKey) {
                      setSelectedColumnType(null)
                      setSelectedColKey(null)
                      setSelectedColBlockId(null)
                    }
                  }}
                  onMoveColBlock={(colKey, blockId, direction) =>
                    moveColBlock(slot.id, colKey, blockId, direction)
                  }
                />
              )
            })}
          </div>
        </SortableContext>
      )}
    </div>
  )
}
